/**
 * Mock Mode Configuration
 *
 * Enables full client-side testing without backend API or Google OAuth.
 * All operations run client-side only, using localStorage for persistence.
 *
 * DESIGNED TO BE REMOVABLE: Delete this file and all /mocks/ folder when no longer needed.
 */

/**
 * Check if mock mode is enabled
 * Priority: Environment variable > localStorage
 *
 * Gated on `import.meta.env.DEV` so the whole branch is tree-shaken out of a
 * production bundle. Without it the `localStorage` operand survives dead-code
 * elimination, and mock mode swaps AuthContext's signature-verified `verifyJWT`
 * for a bare `parseJWT` — so on the deployed app a forged `?token=` plus
 * `localStorage.setItem('visits_mock_mode','true')` would yield an
 * authenticated-looking session with attacker-chosen identity.
 */
/* eslint-disable no-console */
export const MOCK_MODE_ENABLED =
  import.meta.env.DEV &&
  (import.meta.env.VITE_MOCK_MODE === 'true' ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('visits_mock_mode') === 'true'));

/**
 * Enable mock mode and reload page
 */
export function enableMockMode(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('visits_mock_mode', 'true');
    window.location.reload();
  }
}

/**
 * Disable mock mode and reload page
 */
export function disableMockMode(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('visits_mock_mode');
    window.location.reload();
  }
}

/**
 * Get mock mode status (for debugging)
 */
export function getMockModeStatus(): {
  enabled: boolean;
  source: 'environment' | 'localStorage' | 'disabled';
  } {
  // Mirrors the DEV gate on MOCK_MODE_ENABLED so this never reports enabled in a
  // production bundle, where the flag cannot actually take effect.
  if (import.meta.env.DEV) {
    if (import.meta.env.VITE_MOCK_MODE === 'true') {
      return { enabled: true, source: 'environment' };
    }
    if (typeof localStorage !== 'undefined' && localStorage.getItem('visits_mock_mode') === 'true') {
      return { enabled: true, source: 'localStorage' };
    }
  }
  return { enabled: false, source: 'disabled' };
}

/**
 * Clear all mock data from localStorage
 */
export function clearMockData(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('mock_drafts');
    localStorage.removeItem('mock_submissions');
    localStorage.removeItem('mock_current_draft_id');
    console.log('[Mock Mode] All mock data cleared');
  }
}
